/**
 * CommandDock — Cursor-style sticky bottom command bar
 * 핸드오프: docs/handoff/video-editor/project/v3_6/v3-6-home.jsx (CommandDock)
 *
 * 동작:
 * - 프롬프트 입력 + (옵션 expand) 톤·길이·비율 chips
 * - 제출 → /ai 라우트로 이동 (query params 전달)
 */
'use client'

import { useRouter } from 'next/navigation'
import { useState } from 'react'
import { V3Icon } from '@/components/icons/V3Icon'

const TONES = ['자동', '따뜻한', '차가운', '빠른', '감성적', '클린']
const LENGTHS = ['15초', '30초', '1분', '1분+']
const RATIOS = ['9:16', '1:1', '16:9']
const SUGGESTIONS = ['트렌드 BGM', '자막 자동', '30초로', '썸네일 3종']

export default function CommandDock() {
  const router = useRouter()
  const [prompt, setPrompt] = useState('')
  const [tone, setTone] = useState<string>('자동')
  const [length, setLength] = useState<string>('30초')
  const [ratio, setRatio] = useState<string>('9:16')
  const [expanded, setExpanded] = useState(false)

  const submit = () => {
    const params = new URLSearchParams({
      prompt: prompt.trim(),
      tone,
      length,
      ratio,
    })
    router.push(`/ai?${params.toString()}`)
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(720px, calc(100% - 24px))',
        zIndex: 40,
      }}
    >
      {/* 옵션 expand 패널 */}
      {expanded && (
        <div
          className="v36-glass-strong v36-fade-in"
          style={{
            marginBottom: 8,
            padding: 14,
            borderRadius: 18,
            boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          }}
        >
          <div className="v36-label" style={{ marginBottom: 10 }}>
            톤 · 길이 · 비율
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {TONES.map((t) => (
              <button
                key={t}
                className={`v36-chip ${tone === t ? 'v36-chip--active' : ''}`}
                onClick={() => setTone(t)}
              >
                {t}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {LENGTHS.map((t) => (
              <button
                key={t}
                className={`v36-chip ${length === t ? 'v36-chip--active' : ''}`}
                onClick={() => setLength(t)}
              >
                {t}
              </button>
            ))}
            <span style={{ flex: 1 }} />
            {RATIOS.map((t) => (
              <button
                key={t}
                className={`v36-chip v36-mono ${ratio === t ? 'v36-chip--active' : ''}`}
                onClick={() => setRatio(t)}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 메인 바 */}
      <div
        className="v36-glass-strong"
        style={{
          borderRadius: 999,
          padding: 6,
          boxShadow: '0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(124,92,255,0.2)',
          border: '1px solid rgba(255,255,255,0.14)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '0 4px 0 16px' }}>
          <V3Icon name="sparkle" size={18} style={{ color: 'var(--v36-violet)', flexShrink: 0 }} />
          <input
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && prompt.trim()) submit()
            }}
            placeholder="만들고 싶은 영상을 한 줄로…"
            style={{
              flex: 1,
              minWidth: 0,
              height: 44,
              border: 0,
              background: 'transparent',
              fontSize: 15,
              outline: 'none',
              color: '#fff',
            }}
          />
          <button
            className="v36-btn v36-btn--ghost v36-btn--sm"
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? '옵션 닫기' : '옵션 열기'}
            style={{
              width: 36,
              padding: 0,
              color: expanded ? 'var(--v36-violet)' : 'var(--v36-ink-3)',
            }}
          >
            <V3Icon name={expanded ? 'chevD' : 'settings'} size={16} />
          </button>
          <button
            className="v36-btn v36-btn--neon"
            onClick={submit}
            disabled={!prompt.trim()}
            style={{ height: 36, padding: '0 16px', opacity: prompt.trim() ? 1 : 0.5 }}
          >
            <V3Icon name="bolt" size={14} />
            <span>생성</span>
          </button>
        </div>
      </div>

      {/* 추천 chips */}
      {!expanded && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 8, flexWrap: 'wrap' }}>
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              className="v36-chip"
              onClick={() => setPrompt((p) => (p ? `${p} ${s}` : s))}
              style={{ height: 26, fontSize: 12, background: 'rgba(0,0,0,0.4)' }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
